import { extractLanguageFromPath, getCanonicalPath, isCityPage } from '@/utils/routing/multilingualRouting';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { getRouteStatistics } from '@/utils/multilingualRouteGenerator';
import { cityLanguageNames, CityLanguage } from '@/context/languages/languageData';

/**
 * Debug utilities for multilingual routing
 */
export const debugMultilingualRouting = () => {
  const stats = getRouteStatistics();
  
  console.log('🌍 Multilingual Routing Debug Info:');
  console.log('===================================');
  console.log(`Total routes: ${stats.total}`);
  console.log(`Supported languages: ${stats.languages}`);
  console.log(`Cities: ${stats.cities}`);
  console.log(`Services: ${stats.services}`);
  console.log('');
  
  console.log('Routes by language:');
  Object.entries(stats.byLanguage).forEach(([lang, count]) => {
    const langName = cityLanguageNames[lang as CityLanguage] || lang;
    console.log(`  ${lang} (${langName}): ${count} routes`);
  });
  console.log('');
  
  console.log('Routes by type:');
  Object.entries(stats.byType).forEach(([type, count]) => {
    console.log(`  ${type}: ${count} routes`);
  });
  
  return stats;
};

/**
 * Test path parsing
 */
export const testPathParsing = (testPaths: string[]) => {
  console.log('🔍 Testing Path Parsing:');
  console.log('========================');
  
  testPaths.forEach(path => {
    const language = extractLanguageFromPath(path);
    const canonical = getCanonicalPath(path);
    const isCity = isCityPage(path);
    
    console.log(`Path: ${path}`);
    console.log(`  Language: ${language || 'none'}`);
    console.log(`  Canonical: ${canonical}`);
    console.log(`  Is City Page: ${isCity}`);
    console.log('');
  });
};

/**
 * Test city language detection
 */
export const testCityLanguageDetection = (citySlugs: string[]) => {
  console.log('🏙️ Testing City Language Detection:');
  console.log('===================================');
  
  // This would need access to city data, but for now just log the process
  citySlugs.forEach(slug => {
    console.log(`City: ${slug}`);
    console.log(`  Detected language: ${detectCityLanguage({ slug } as any)}`);
  });
};

/**
 * Run all debugging tests
 */
export const runMultilingualDebugSuite = () => {
  console.log('🚀 Running Multilingual Debug Suite');
  console.log('====================================');
  
  // Basic stats
  debugMultilingualRouting();
  
  // Test common paths
  const testPaths = [
    '/city/madrid',
    '/es/city/madrid',
    '/city/paris',
    '/fr/city/paris',
    '/city/london',
    '/city/tokyo',
    '/ja/city/tokyo',
    '/zh/city/beijing',
    '/ar/city/cairo'
  ];
  
  testPathParsing(testPaths);
  
  // Test city language detection
  const testCities = ['madrid', 'paris', 'london', 'tokyo', 'beijing', 'cairo'];
  testCityLanguageDetection(testCities);
  
  console.log('✅ Debug suite completed');
};

/**
 * Get language coverage report
 */
export const getLanguageCoverageReport = () => {
  const stats = getRouteStatistics();
  const allLanguages = Object.keys(cityLanguageNames);
  
  const coverage = {
    totalLanguages: allLanguages.length,
    activeLanguages: Object.keys(stats.byLanguage).length,
    coveragePercentage: (Object.keys(stats.byLanguage).length / allLanguages.length) * 100,
    missingLanguages: allLanguages.filter(lang => !stats.byLanguage[lang]),
    routeDistribution: stats.byLanguage
  };
  
  console.log('📊 Language Coverage Report:');
  console.log('============================');
  console.log(`Coverage: ${coverage.coveragePercentage.toFixed(1)}%`);
  console.log(`Active: ${coverage.activeLanguages}/${coverage.totalLanguages} languages`);
  
  if (coverage.missingLanguages.length > 0) {
    console.log('Missing languages:', coverage.missingLanguages.join(', '));
  }
  
  return coverage;
};